import { NotificationEnum } from "@graphql/generated/graphql.g";
import dayjs from "dayjs";
import { ComponentProps, forwardRef, useImperativeHandle } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { InfiniteClientSelect } from "@components/Selects/InfiniteClientSelect";
import { InfiniteUserSelect } from "@components/Selects/InfiniteUserSelect";
import { NOTIFICATION_TYPE_OPTIONS } from "@utils/notificationTypes";

import { Button } from "@design-system/Button/Button";
import { DateRangePicker } from "@design-system/DateRangePicker/DateRangePicker";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { Select } from "@design-system/Select/Select";
import { TextField } from "@design-system/TextField/TextField";

type DateRange = [Date | null, Date | null];

const dateRangeToFormValues = (
  range: DateRange,
): { resolvedAt_Gte?: string; resolvedAt_Lte?: string } => {
  const [startDate, endDate] = range;
  return {
    resolvedAt_Gte: startDate
      ? dayjs(startDate).startOf("day").toISOString()
      : undefined,
    resolvedAt_Lte: endDate
      ? dayjs(endDate).endOf("day").toISOString()
      : undefined,
  };
};

const formValuesToDateRange = (
  resolvedAt_Gte?: string,
  resolvedAt_Lte?: string,
): DateRange => {
  return [
    resolvedAt_Gte ? dayjs(resolvedAt_Gte).toDate() : null,
    resolvedAt_Lte ? dayjs(resolvedAt_Lte).toDate() : null,
  ];
};

export type NotificationFilterFormValues = {
  clientId?: string | undefined;
  notificationType?: NotificationEnum | undefined;
  applicationNameSearch?: string | undefined;
  resolvedById?: string | undefined;
  resolvedAt_Gte?: string | undefined;
  resolvedAt_Lte?: string | undefined;
};

export type NotificationFilterFormRef = {
  getCurrentValues: () => NotificationFilterFormValues;
};

type Props = {
  submitText?: string;
  onFilterChange: SubmitHandler<NotificationFilterFormValues>;
  values: NotificationFilterFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
  showHistoryFilters?: boolean;
};

export const NotificationFilterForm = forwardRef<
  NotificationFilterFormRef,
  Props
>(({ onFilterChange, values, errorsAPI, showHistoryFilters = false }, ref) => {
  const { handleSubmit, setValue, watch, getValues } =
    useForm<NotificationFilterFormValues>({
      values: {
        clientId: values.clientId ?? undefined,
        notificationType: values.notificationType ?? undefined,
        applicationNameSearch: values.applicationNameSearch ?? undefined,
        resolvedById: values.resolvedById ?? undefined,
        resolvedAt_Gte: values.resolvedAt_Gte ?? undefined,
        resolvedAt_Lte: values.resolvedAt_Lte ?? undefined,
      },
    });

  const notificationTypeOptions: Array<{
    id: NotificationEnum | "";
    label: string;
  }> = [{ id: "", label: "Not selected" }, ...NOTIFICATION_TYPE_OPTIONS];

  const clientId = watch("clientId");
  const notificationType = watch("notificationType");
  const applicationNameSearch = watch("applicationNameSearch");
  const resolvedById = watch("resolvedById");
  const resolvedAt_Gte = watch("resolvedAt_Gte");
  const resolvedAt_Lte = watch("resolvedAt_Lte");

  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => getValues(),
    }),
    [getValues],
  );

  const createHandleChange = (fieldName: keyof NotificationFilterFormValues) => {
    return (value: string | string[]) => {
      const enumValue = Array.isArray(value) ? value[0] : value;
      const finalValue = enumValue === "" ? undefined : enumValue;
      setValue(fieldName, finalValue as never, { shouldDirty: true });
    };
  };

  const handleDateRangeChange = (range: DateRange) => {
    const { resolvedAt_Gte, resolvedAt_Lte } = dateRangeToFormValues(range);
    setValue("resolvedAt_Gte", resolvedAt_Gte, { shouldDirty: true });
    setValue("resolvedAt_Lte", resolvedAt_Lte, { shouldDirty: true });
  };

  return (
    <Form onSubmit={handleSubmit(onFilterChange)} errorsAPI={errorsAPI}>
      <FieldGroup label="Client">
        <InfiniteClientSelect
          value={clientId}
          onChange={(value) =>
            setValue("clientId", value, { shouldDirty: true })
          }
          placeholder="Search and select client..."
        />
      </FieldGroup>

      <FieldGroup label="Notification type">
        <Select
          options={notificationTypeOptions}
          value={notificationType ?? ""}
          onChange={createHandleChange("notificationType")}
        />
      </FieldGroup>

      <FieldGroup label="Application name">
        <TextField
          placeholder="Search by application name..."
          value={applicationNameSearch ?? ""}
          onChange={(e) =>
            setValue("applicationNameSearch", e.target.value || undefined, {
              shouldDirty: true,
            })
          }
        />
      </FieldGroup>

      {showHistoryFilters && (
        <>
          <FieldGroup label="Resolved by">
            <InfiniteUserSelect
              value={resolvedById}
              onChange={(value: string | undefined) =>
                setValue("resolvedById", value, { shouldDirty: true })
              }
              placeholder="Search and select user..."
            />
          </FieldGroup>

          <FieldGroup label="Resolved between">
            <DateRangePicker
              fullWidth
              value={formValuesToDateRange(resolvedAt_Gte, resolvedAt_Lte)}
              onChange={handleDateRangeChange}
              placeholder="Select date range"
            />
          </FieldGroup>
        </>
      )}

      <Button type="submit" style={{ display: "none" }} />
    </Form>
  );
});
